import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (event.detail?.localVariant) {
      this.#updateFromLocalVariant(event.detail.localVariant);
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };

  /**
   * Updates simple product-page pricing immediately while the server response reconciles state.
   * Products with unit or volume pricing do not emit a local state map.
   *
   * @param {NonNullable<import('@shopify/events').ProductSelectPayloadDetail['localVariant']>} variant
   */
  #updateFromLocalVariant(variant) {
    const currentPrice = this.refs.priceContainer;
    if (!currentPrice) return;

    const priceContainer = document.createElement('div');
    priceContainer.setAttribute('ref', 'priceContainer');

    if (variant.compareAtPrice) {
      const saleFirst = this.dataset.showSalePriceFirst === 'true';
      const salePrice = this.#createPriceGroup(variant.price, 'price', this.dataset.priceSaleLabel);
      const regularPrice = this.#createPriceGroup(
        variant.compareAtPrice,
        'price-item__group price-item--regular compare-at-price',
        this.dataset.priceRegularLabel
      );

      priceContainer.append(...(saleFirst ? [salePrice, regularPrice] : [regularPrice, salePrice]));
    } else {
      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = variant.price;
      priceContainer.append(price);
    }

    currentPrice.replaceWith(priceContainer);
  }

  /**
   * @param {string} value
   * @param {string} className
   * @param {string | undefined} accessibleLabel
   */
  #createPriceGroup(value, className, accessibleLabel) {
    const group = document.createElement('span');
    group.className = className;

    if (accessibleLabel) {
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `${accessibleLabel}\u00a0`;
      group.append(label);
    }

    group.append(document.createTextNode(value));
    return group;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
